import { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth } from "../../../../context/AuthContext";

const initialMembershipForm = {
  plan_id: "",
  selected_accesses: [],
  start_date: "",
  end_date: "",
  price_paid: "",
  visits_total: "",
  notes: "",
};

export default function useMembersPanelLogic() {
  const { authFetch, user } = useAuth();

  const [users, setUsers] = useState([]);
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [actionLoadingId, setActionLoadingId] = useState("");
  const [banner, setBanner] = useState({
    type: "",
    message: "",
  });
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  const [membershipOpen, setMembershipOpen] = useState(false);
  const [membershipLoading, setMembershipLoading] = useState(false);
  const [membershipSubmitting, setMembershipSubmitting] = useState(false);
  const [membershipUser, setMembershipUser] = useState(null);
  const [memberMemberships, setMemberMemberships] = useState([]);
  const [activeMembership, setActiveMembership] = useState(null);
  const [plans, setPlans] = useState([]);
  const [membershipForm, setMembershipForm] = useState(initialMembershipForm);

  const canManageUsers = user?.role === "admin";
  const canManageMemberships = user?.role === "admin" || user?.role === "staff";

  const resetMembershipForm = useCallback(() => {
    setMembershipForm(initialMembershipForm);
  }, []);

  const loadMembers = useCallback(async () => {
    setLoading(true);
    setBanner({ type: "", message: "" });

    try {
      const response = await authFetch("/api/gym/admin/users/", {
        method: "GET",
      });

      const data = await response.json().catch(() => []);

      if (!response.ok) {
        setBanner({
          type: "error",
          message: data?.error || "No se pudieron cargar los usuarios",
        });
        setUsers([]);
        return;
      }

      const members = Array.isArray(data)
        ? data.filter((item) => item.role === "member")
        : [];

      setUsers(members);
    } catch {
      setBanner({
        type: "error",
        message: "Ocurrió un error al cargar los usuarios",
      });
      setUsers([]);
    } finally {
      setLoading(false);
    }
  }, [authFetch]);

  useEffect(() => {
    loadMembers();
  }, [loadMembers]);

  const filteredUsers = useMemo(() => {
    const term = query.trim().toLowerCase();

    if (!term) return users;

    return users.filter((item) => {
      const fullName = `${item.first_name || ""} ${item.last_name || ""}`.trim().toLowerCase();
      const email = String(item.email || "").toLowerCase();
      const phone = String(item.phone || "").toLowerCase();

      return fullName.includes(term) || email.includes(term) || phone.includes(term);
    });
  }, [users, query]);

  const getFullName = useCallback(
    (item) => `${item.first_name || ""} ${item.last_name || ""}`.trim() || "Sin nombre",
    []
  );

  const getInitial = useCallback(
    (item) => {
      const fullName = getFullName(item);
      return fullName.charAt(0).toUpperCase();
    },
    [getFullName]
  );

  const getPhoto = useCallback((item) => item?.profile?.profile_photo || "", []);

  const selectedPlan = useMemo(
    () => plans.find((plan) => String(plan.id) === String(membershipForm.plan_id)) || null,
    [plans, membershipForm.plan_id]
  );

  return {
    authFetch,
    users,
    setUsers,
    query,
    setQuery,
    loading,
    actionLoadingId,
    setActionLoadingId,
    banner,
    setBanner,
    deleteOpen,
    setDeleteOpen,
    selectedUser,
    setSelectedUser,
    canManageUsers,
    canManageMemberships,
    loadMembers,
    filteredUsers,
    getFullName,
    getInitial,
    getPhoto,
    membershipOpen,
    setMembershipOpen,
    membershipLoading,
    setMembershipLoading,
    membershipSubmitting,
    setMembershipSubmitting,
    membershipUser,
    setMembershipUser,
    memberMemberships,
    setMemberMemberships,
    activeMembership,
    setActiveMembership,
    plans,
    setPlans,
    membershipForm,
    setMembershipForm,
    resetMembershipForm,
    selectedPlan,
  };
}